import React, { useState, useCallback, useEffect } from "react";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import { generateAiReport } from "../services/aiReportsService";
import "../styles/ChatContainer.css";
import { useSelector } from "react-redux";
import { validateAnalysisContext } from "../utils/analysisContextUtils";
import { Toast } from "../../../helpers/alert";

const REPORT_TYPE_LABELS = {
  costs: "costos",
  consumption: "consumo",
  complete: "completo de consumo y costos",
  both: "completo de consumo y costos",
  energy_matrix: "matriz energetica",
  calculated_parameter: "parametros calculados",
};

const padMonth = (value) => {
  const number = Number(value);
  if (!Number.isInteger(number) || number < 1 || number > 12) return "";
  return String(number).padStart(2, "0");
};

const getLastDayOfMonth = (year, month) =>
  String(new Date(Number(year), Number(month), 0).getDate()).padStart(2, "0");

const normalizeSelectedPeriod = (period = {}) => {
  if (period?.mode === "custom" && period.startDate && period.endDate) {
    const match = String(period.startDate).match(/^(\d{4})-(\d{2})-\d{2}$/);
    const year = match?.[1] || "";
    const month = match?.[2] || "";
    return {
      period: year && month ? `${year}-${month}` : "",
      month,
      year,
      startDate: period.startDate,
      endDate: period.endDate,
    };
  }

  const month = padMonth(period?.month);
  const year = period?.year ? String(period.year) : "";

  if (!month || !year) {
    return {
      period: "",
      month: "",
      year: "",
      startDate: "",
      endDate: "",
    };
  }

  return {
    period: `${year}-${month}`,
    month,
    year,
    startDate: `${year}-${month}-01`,
    endDate: `${year}-${month}-${getLastDayOfMonth(year, month)}`,
  };
};

const getBranchName = (branch) => {
  if (!branch) return "todas mis sucursales";
  if (typeof branch === "string") return branch;
  return branch.name || branch.office_name || branch.nombres || "todas mis sucursales";
};

const getBranchId = (branch) => {
  const id = branch?.id || branch?.id_branch_offices || branch?.branchId;
  return id && id !== "all" ? id : null;
};

const getCompanyId = (branch, context = {}) =>
  context.companyId ||
  context.company_id ||
  branch?.companyId ||
  branch?.company_id ||
  branch?.company_fk ||
  branch?.id_company ||
  null;

const buildSelectedReportContext = (context = {}) => {
  const normalizedPeriod = normalizeSelectedPeriod(context.period);
  const branchName = getBranchName(context.branch);
  const branchId = getBranchId(context.branch);
  const deviceId = context.device?.id || context.device?.id_device || null;
  const reportType = context.analysisType || context.reportType || "";

  return {
    reportType,
    analysisType: context.analysisType,
    branch: branchName,
    branchId,
    companyId: getCompanyId(context.branch, context),
    device: context.device?.name || null,
    deviceId,
    mac: context.device?.mac || null,
    metric: context.metric || null,
    calculatedParameter: context.calculatedParameter || null,
    period: normalizedPeriod.period,
    month: normalizedPeriod.month,
    year: normalizedPeriod.year,
    startDate: normalizedPeriod.startDate,
    endDate: normalizedPeriod.endDate,
  };
};

const buildVisibleContextMessage = (context = {}) => {
  const normalized = buildSelectedReportContext(context);
  const reportLabel =
    REPORT_TYPE_LABELS[normalized.reportType] || normalized.reportType || "IA";
  const branchLabel = normalized.branch || "todas mis sucursales";
  const periodLabel = normalized.period || "periodo seleccionado";

  return `Generar informe de ${reportLabel} para ${branchLabel} - período ${periodLabel}.`;
};

const ChatContainer = ({ analysisContext, onRegisterSend }) => {
  const currentUser = useSelector((state) => state.user?.currentUser);
  const userId = currentUser?.id_user;
  const token = currentUser?.accessToken;
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUserStartedChat, setHasUserStartedChat] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("sigerbot_session_")) {
        localStorage.removeItem(key);
      }
    });
    setMessages([]);
    setHasUserStartedChat(false);
    setSessionId(null);
  }, [userId]);

  const getLastAssistantMessageIndex = useCallback(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        return i;
      }
    }
    return -1;
  }, [messages]);

  /**
   * Convierte la respuesta del API en el formato esperado por los componentes
   * Mantiene solo los campos que el agente devuelve, eliminando propiedades innecesarias
   */
  const parseApiResponse = (apiData) => {
    // Si viene como string simple, lo convertimos en objeto
    if (typeof apiData === "string") {
      return {
        message: apiData,
      };
    }

    // Si es un objeto, extraemos solo los campos relevantes
    if (apiData && typeof apiData === "object") {
      const parsed = {};

      // El agente envía el contenido principal en 'result' (desde el backend), 'message' o 'content'
      if (apiData.result) {
        parsed.message = apiData.result;
      } else if (apiData.message) {
        parsed.message = apiData.message;
      } else if (apiData.content) {
        parsed.message = apiData.content;
      }

      // Si hay título, lo incluimos
      if (apiData.title) parsed.title = apiData.title;

      // Si hay estructura adicional (KPIs, observaciones, recomendaciones), la incluimos
      if (
        apiData.kpis &&
        Array.isArray(apiData.kpis) &&
        apiData.kpis.length > 0
      ) {
        parsed.kpis = apiData.kpis;
      }
      if (
        apiData.observations &&
        Array.isArray(apiData.observations) &&
        apiData.observations.length > 0
      ) {
        parsed.observations = apiData.observations;
      }
      if (
        apiData.recommendations &&
        Array.isArray(apiData.recommendations) &&
        apiData.recommendations.length > 0
      ) {
        parsed.recommendations = apiData.recommendations;
      }

      if (
        apiData.artifacts &&
        Array.isArray(apiData.artifacts) &&
        apiData.artifacts.length > 0
      ) {
        parsed.artifacts = apiData.artifacts;
      }

      if (
        apiData.actions &&
        Array.isArray(apiData.actions) &&
        apiData.actions.length > 0
      ) {
        parsed.actions = apiData.actions;
      }

      if (apiData.metadata && typeof apiData.metadata === "object") {
        parsed.metadata = apiData.metadata;
      }

      if (
        apiData.files &&
        Array.isArray(apiData.files) &&
        apiData.files.length > 0
      ) {
        parsed.files = apiData.files;
      }

      // Si no hay 'message' pero sí 'summary', lo usamos como mensaje
      if (!parsed.message && apiData.summary) {
        parsed.message = apiData.summary;
      }

      // Si aún no hay mensaje pero sí hay otros datos, avisar
      if (!parsed.message) {
        console.warn(
          "⚠️ No se encontró contenido en la respuesta. Data disponible:",
          Object.keys(apiData),
        );
        parsed.message =
          "✅ Informe generado. (Los detalles se cargarán en breve)";
      }

      return parsed;
    }

    return {
      message: "No se pudo procesar la respuesta del agente",
    };
  };

  const handleSendMessage = useCallback(
    async (messageContent) => {
      // Marcar que el usuario comenzó a chatear (en el primer mensaje)
      if (!hasUserStartedChat) {
        setHasUserStartedChat(true);
      }

      const sanitizedContent = messageContent?.trim();
      const selectedReportContext = analysisContext?.analysisType
        ? buildSelectedReportContext(analysisContext)
        : null;
      const visibleUserMessage =
        sanitizedContent ||
        (analysisContext?.analysisType
          ? buildVisibleContextMessage(analysisContext)
          : "Generar informe de IA con el contexto seleccionado.");

      // Si hay contexto, validar y construir prompt enriquecido
      if (analysisContext?.analysisType) {
        const { isValid, errors } = validateAnalysisContext(analysisContext);
        if (!isValid) {
          Toast.fire({
            icon:"error",
            title: `Por favor completa el contexto. Errores: ${errors.join(", ")}`,
          });
          return;
        }
      } else if (!sanitizedContent) {
        // Si no hay contexto ni mensaje, pedir que escriba algo
        Toast.fire({
          icon:"warning",
          title: "Escribí una consulta o selecciona un tipo de análisis",
        });
        return;
      }

      const userMessage = {
        id: Date.now(),
        role: "user",
        content: visibleUserMessage,
        createdAt: new Date().toISOString(),
      };

      const pendingAssistantId = Date.now() + 1;
      const pendingAssistantMessage = {
        id: pendingAssistantId,
        role: "assistant",
        type: "typing",
        content: { message: "" },
        createdAt: new Date().toISOString(),
        status: "pending",
        isFinal: false,
      };

      const requestHistory = messages.filter((message) => message.status !== "pending");

      setMessages((prev) => [...prev, userMessage, pendingAssistantMessage]);
      setIsLoading(true);

      try {
        // Llamar al servicio real con sessionId y el prompt final (con contexto si aplica)
        const response = await generateAiReport(
          visibleUserMessage,
          sessionId,
          userId,
          selectedReportContext || analysisContext,
          token,
          analysisContext,
          requestHistory,
        );

        if (response.success) {
          // Mantener sessionId solo en estado React durante la pantalla actual.
          const signedSessionId = response.data?.sessionId;

          if (signedSessionId) {
            setSessionId(signedSessionId);
          }

          // Crear mensaje del asistente con datos reales de la API
          const parsedContent = parseApiResponse(response.data);

          const assistantMessage = {
            id: pendingAssistantId,
            role: "assistant",
            type: "structured-report",
            content: parsedContent,
            createdAt: new Date().toISOString(),
            status: "done",
            isFinal: false,
            metadata: response.data?.metadata || {},
          };

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === pendingAssistantId ? assistantMessage : msg,
            ),
          );
        } else {
          // Mostrar mensaje de error como respuesta del asistente
          const errorMessage = {
            id: pendingAssistantId,
            role: "assistant",
            type: "error",
            content: {
              message:
                response.message ||
                "No se pudo generar el informe. Por favor, intenta nuevamente.",
            },
            createdAt: new Date().toISOString(),
            status: "done",
            isFinal: false,
          };

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === pendingAssistantId ? errorMessage : msg,
            ),
          );
        }
      } catch (error) {
        console.error("❌ Error en handleSendMessage:", error);

        // Mostrar error general
        const errorMessage = {
          id: pendingAssistantId,
          role: "assistant",
          type: "error",
          content: {
            message:
              "Ocurrió un error al procesar tu solicitud. Por favor, intenta nuevamente.",
          },
          createdAt: new Date().toISOString(),
          status: "done",
          isFinal: false,
        };

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === pendingAssistantId ? errorMessage : msg,
          ),
        );
      } finally {
        setIsLoading(false);
      }
    },
    [analysisContext, sessionId, token, userId, hasUserStartedChat, messages],
  );

  useEffect(() => {
    if (typeof onRegisterSend === "function") {
      onRegisterSend(handleSendMessage);
    }
  }, [handleSendMessage, onRegisterSend]);

  const handleSuggestionClick = (suggestionPrompt) => {
    handleSendMessage(suggestionPrompt);
  };

  const handleNewChat = () => {
    const confirmed =
      messages.length > 0
        ? window.confirm(
            "¿Estás seguro de que querés iniciar una nueva conversación? Se perderá el historial actual.",
          )
        : true;

    if (confirmed) {
      setMessages([]);
      setHasUserStartedChat(false);
      setSessionId(null);
    }
  };

  return (
    <div className="chat-container">
      <MessageList
        messages={messages}
        onSuggestionClick={handleSuggestionClick}
        lastAssistantMessageIndex={getLastAssistantMessageIndex()}
        hasUserStartedChat={hasUserStartedChat}
      />
      <ChatInput
        onSendMessage={handleSendMessage}
        onNewChat={handleNewChat}
        isLoading={isLoading}
      />
    </div>
  );
};

export default ChatContainer;
